const STORAGE_KEYS = {
  accessToken: "accessToken",
  refreshToken: "refreshToken",
  companyId: "companyId",
  companyCode: "companyCode",
  companyName: "companyName",
  companyNameEn: "companyNameEn",
  email: "email",
  passwordTime: "passwordTime",
  logo: "logo",
  logoVisibleType: "logoVisibleType",
  isEmailVisible: "isEmailVisible",
  departmentVisible: "departmentVisible",
  emailVisible: "emailVisible",
  updatedLogo: "updatedLogo",
  logoUpdatedFlag: "logo-updated",
};

const getStorageItem = (key) => localStorage.getItem(key);
const removeStorageItem = (key) => localStorage.removeItem(key);

const setStorageItem = (key, value) => {
  if (value === undefined || value === null) {
    return;
  }

  localStorage.setItem(key, value);
};

export const getAuthSession = () => ({
  accessToken: getStorageItem(STORAGE_KEYS.accessToken),
});

export const getCompanyProfile = () => ({
  companyId: getStorageItem(STORAGE_KEYS.companyId),
  companyCode: getStorageItem(STORAGE_KEYS.companyCode),
  companyName: getStorageItem(STORAGE_KEYS.companyName),
  companyNameEn: getStorageItem(STORAGE_KEYS.companyNameEn),
  email: getStorageItem(STORAGE_KEYS.email),
  passwordTime: getStorageItem(STORAGE_KEYS.passwordTime),
  logo: getStorageItem(STORAGE_KEYS.logo),
  logoVisibleType: getStorageItem(STORAGE_KEYS.logoVisibleType),
  isEmailVisible: getStorageItem(STORAGE_KEYS.isEmailVisible),
  departmentVisible: getStorageItem(STORAGE_KEYS.departmentVisible),
  emailVisible: getStorageItem(STORAGE_KEYS.emailVisible),
});

export const getDashboardUiState = () => ({
  updatedLogo: getStorageItem(STORAGE_KEYS.updatedLogo),
  logoUpdatedFlag: getStorageItem(STORAGE_KEYS.logoUpdatedFlag),
});

export const getDashboardSession = () => ({
  ...getAuthSession(),
  ...getCompanyProfile(),
  ...getDashboardUiState(),
});

export const getCompanyAuthPayload = () => {
  const { companyId, companyCode } = getCompanyProfile();

  return { companyId, companyCode };
};

export const setAuthSession = (sessionValues = {}) => {
  setStorageItem(STORAGE_KEYS.accessToken, sessionValues.accessToken);
  removeStorageItem(STORAGE_KEYS.refreshToken);
};

export const setCompanyProfile = (sessionValues = {}) => {
  setStorageItem(STORAGE_KEYS.companyId, sessionValues.companyId);
  setStorageItem(STORAGE_KEYS.companyCode, sessionValues.companyCode);
  setStorageItem(STORAGE_KEYS.companyName, sessionValues.companyName);
  setStorageItem(STORAGE_KEYS.companyNameEn, sessionValues.companyNameEn);
  setStorageItem(STORAGE_KEYS.email, sessionValues.email);
  setStorageItem(STORAGE_KEYS.passwordTime, sessionValues.passwordTime);
  setStorageItem(STORAGE_KEYS.logo, sessionValues.logo);
  setStorageItem(STORAGE_KEYS.logoVisibleType, sessionValues.logoVisibleType);
  setStorageItem(STORAGE_KEYS.isEmailVisible, sessionValues.isEmailVisible);
  setStorageItem(STORAGE_KEYS.departmentVisible, sessionValues.departmentVisible);
  setStorageItem(STORAGE_KEYS.emailVisible, sessionValues.emailVisible);
};

export const setDashboardUiState = (sessionValues = {}) => {
  setStorageItem(STORAGE_KEYS.updatedLogo, sessionValues.updatedLogo);
  setStorageItem(STORAGE_KEYS.logoUpdatedFlag, sessionValues.logoUpdatedFlag);
};

export const setDashboardSession = (sessionValues) => {
  setAuthSession(sessionValues);
  setCompanyProfile(sessionValues);
  setDashboardUiState(sessionValues);
};

export const clearAuthSession = () => {
  removeStorageItem(STORAGE_KEYS.accessToken);
  removeStorageItem(STORAGE_KEYS.refreshToken);
};

export const clearCompanyProfile = () => {
  removeStorageItem(STORAGE_KEYS.companyId);
  removeStorageItem(STORAGE_KEYS.companyCode);
  removeStorageItem(STORAGE_KEYS.companyName);
  removeStorageItem(STORAGE_KEYS.companyNameEn);
  removeStorageItem(STORAGE_KEYS.email);
  removeStorageItem(STORAGE_KEYS.passwordTime);
  removeStorageItem(STORAGE_KEYS.logo);
  removeStorageItem(STORAGE_KEYS.logoVisibleType);
  removeStorageItem(STORAGE_KEYS.isEmailVisible);
  removeStorageItem(STORAGE_KEYS.departmentVisible);
  removeStorageItem(STORAGE_KEYS.emailVisible);
};

export const clearDashboardUiState = () => {
  removeStorageItem(STORAGE_KEYS.updatedLogo);
  removeStorageItem(STORAGE_KEYS.logoUpdatedFlag);
};

export const clearDashboardSession = () => {
  clearAuthSession();
  clearCompanyProfile();
  clearDashboardUiState();
};

export const setCompanyName = (companyName) => {
  setStorageItem(STORAGE_KEYS.companyName, companyName);
};

export const setCompanyNames = ({
  companyName,
  companyNameEn,
} = {}) => {
  setStorageItem(STORAGE_KEYS.companyName, companyName);
  setStorageItem(STORAGE_KEYS.companyNameEn, companyNameEn);
};

export const getDashboardLogoState = () => {
  const { logo } = getCompanyProfile();
  const { updatedLogo, logoUpdatedFlag } = getDashboardUiState();
  const isLogoUpdated = logoUpdatedFlag === "true";

  return {
    logo,
    updatedLogo,
    isLogoUpdated,
  };
};

export const getDashboardResolvedLogoSrc = () => {
  const { logo, updatedLogo, isLogoUpdated } = getDashboardLogoState();
  const resolvedLogo = isLogoUpdated ? updatedLogo : logo;

  if (!resolvedLogo || resolvedLogo === "null") {
    return null;
  }

  return isLogoUpdated
    ? resolvedLogo
    : `${process.env.REACT_APP_IMAGE_BASE_URL}/${resolvedLogo}`;
};

export const setUpdatedDashboardLogo = (updatedLogo) => {
  setStorageItem(STORAGE_KEYS.updatedLogo, updatedLogo);
};

export const setDashboardLogoUpdatedFlag = (isUpdated) => {
  if (isUpdated) {
    setStorageItem(STORAGE_KEYS.logoUpdatedFlag, "true");
    return;
  }

  removeStorageItem(STORAGE_KEYS.logoUpdatedFlag);
};

export const clearDashboardLogoState = ({ removeStoredLogo = false } = {}) => {
  if (removeStoredLogo) {
    removeStorageItem(STORAGE_KEYS.logo);
  }

  removeStorageItem(STORAGE_KEYS.updatedLogo);
  removeStorageItem(STORAGE_KEYS.logoUpdatedFlag);
};
